import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {BrandFont} from '@Components/editor-brands/editor-brands.types';
import type {FontData} from '@Libraries/font-library';
import {isInternalFont, USER_FONT_LICENSE} from '@Libraries/font-library';
import {PosterEditingBaseFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-font-selector';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';

interface TextItemFontSelectorProps {
  onBack?(): void;
  onClose?(): void;
  showCloseButton?: boolean;
  showBackButton?: boolean;
  isForMobile?: boolean;
}

export function TextItemFontSelector({
  isForMobile = false,
  onClose = (): void => {},
  onBack = (): void => {},
  showCloseButton = false,
  showBackButton = true,
}: TextItemFontSelectorProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemFontFamily = useGetKeyFromTextStylesActiveItem('fontFamily');
  const itemScript = useGetKeyFromTextStylesActiveItem('script');
  const itemFontLicense = useGetKeyFromTextStylesActiveItem('fontLicense');

  const [temporaryState, setTemporaryState] = useState<string>('');
  const [isNewFontLoading, setIsNewFontLoading] = useState(false);

  if (!activeItem || (activeItem && !('textStyles' in activeItem)) || itemFontFamily === undefined || itemScript === undefined || itemFontLicense === undefined) {
    throw new Error('Item does not have fontFamily attribute');
  }

  useEffect(() => {
    setTemporaryState(itemFontFamily);
  }, [itemFontFamily]);

  const onSelectFontFamily = (fontFamily: string): void => {
    setIsNewFontLoading(true);
    setTemporaryState(fontFamily);
    const fontLicense = isInternalFont(fontFamily) ? '' : USER_FONT_LICENSE;
    const oldFontFamily = itemFontFamily;
    const oldFontLicense = itemFontLicense;
    activeItem
      .updateFromObject({
        textStyles: {fontFamily, fontLicense},
      })
      .then((): void => {})
      .catch((): void => {
        activeItem.textStyles.fontFamily = oldFontFamily;
        activeItem.textStyles.fontLicense = oldFontLicense;
        setTemporaryState(itemFontFamily);
      })
      .finally((): void => {
        setIsNewFontLoading(false);
      });
  };

  return (
    <PosterEditingBaseFontSelector
      fontsInDesign={window.posterEditor?.whiteboard?.getFontsOnPoster() ?? []}
      onBrandItemClicked={(brand: BrandFont): void => {
        onSelectFontFamily(brand.fontFamily);
      }}
      isSelectedFontLoading={isNewFontLoading}
      selectedFontFamily={isNewFontLoading ? temporaryState : itemFontFamily}
      onSelectFontFamily={(font: FontData): void => {
        onSelectFontFamily(font.fontFamily);
      }}
      scriptForRecommendedFonts={itemScript}
      showHeader
      onBackButtonCallback={(): void => {
        onBack();
      }}
      onAddBrandAsset={(): void => {
        void onAddBrandAssetClickedHandler();
      }}
      onCloseButtonCallback={(): void => {
        onClose();
      }}
      showCloseButton={showCloseButton}
      showBackButton={showBackButton}
      hasSmallTopPadding={isForMobile}
    />
  );
}
